import React, { useRef, useState, useMemo } from 'react'
import './import-record.scss'
import { BaseDrawer } from '@/components/drawer/base-drawer'
import { layerManage, showLayer } from '@/utils/layer'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Row, Expaned } from '@/components/layout/layout'
import $msg from '@/utils/message'
import { $post } from '@/api'
import { useRequest } from 'ahooks'
import { IPagination } from '@/model/types/common'
import { IUser } from '@/model/types/user'
import { ItableKeysMap } from '../table-import/table-import'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import { Table, Empty } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import StaffDetail from '@/views/main/modules/staff-manage/staff-list/staff-detail/staff-detail'

interface IImportRecord {
    create_time: string
    goods_imported: number
    id: number
    source: number
    status: number
    creator: {
        id: number
        user: IUser
    }
}

interface IRecordGoodData extends ItableKeysMap {
    second_tier_threshold: number
    third_tier_threshold: number
}

interface importRecordProps {}

const ImportRecord: React.FC<importRecordProps> = props => {
    const cls = useBemClass('import-record')

    const containerRef = useRef<HTMLDivElement | null>(null)
    const [activeIndex, setActiveIndex] = useState<number>(0)

    const requestHandle = useRequest<
        {
            list: IImportRecord[]
            pagination: IPagination
        },
        {
            records: IImportRecord[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/goods/migration/goodsimportedrecords',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            ref: containerRef,
            formatResult: data => {
                return {
                    list: data.records,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const currentRecord = useMemo(() => {
        return requestHandle.data?.list[activeIndex]
    }, [activeIndex, requestHandle.data])

    const detailReq = useRequest<
        {
            list: IRecordGoodData[]
            pagination: IPagination
        },
        {
            goods: IRecordGoodData[]
            pagination: IPagination
        }
    >(
        val => {
            return currentRecord?.id
                ? $post({
                      url: '/goods/migration/goodsimportedrecordsdetail',
                      params: {
                          limit: 20,
                          page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                          record_id: currentRecord.id,
                      },
                  })
                : new Promise(() => {})
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.goods,
                    pagination: data.pagination,
                }
            },
            refreshDeps: [currentRecord],
            onError: e => {
                $msg('error', e.message)
            },
        },
    )

    const columns: ColumnsType<IRecordGoodData> = [
        {
            title: '一级分组',
            dataIndex: 'primary_group_name',
            width: 130,
        },
        {
            title: '二级分组',
            dataIndex: 'secondary_group_name',
            width: 130,
            render: val => val || '-',
        },
        {
            title: '商品名称',
            dataIndex: 'goods_name',
            width: 130,
        },
        {
            title: '商品类目',
            dataIndex: 'goods_category',
            width: 130,
        },
        {
            title: '进价',
            dataIndex: 'purchase_price',
            render: (val, record) => (val ? `${val}元/${record.unit_name}` : '-'),
            width: 130,
        },
        {
            title: '零售价',
            dataIndex: 'first_tier_price',
            render: (val, record) => `${val}元/${record.unit_name}`,
            width: 130,
        },
        {
            title: '二级阶梯价',
            dataIndex: 'second_tier_price',
            width: 130,
            render: (val: number | null, record) => {
                return val ? `${record.second_tier_threshold}元，${val}元/${record.unit_name}` : '-'
            },
        },
        {
            title: '三级阶梯价',
            dataIndex: 'third_tier_price',
            width: 130,
            render: (val: string, record) => {
                return val ? `${record.third_tier_threshold}元，${val}元/${record.unit_name}` : '-'
            },
        },
        {
            title: '会员价',
            width: 130,
            dataIndex: 'member_price',
            render: (val, record) => (val ? `${val}元/${record.unit_name}` : '-'),
        },
        {
            title: '销售单位',
            width: 130,
            dataIndex: 'unit_name',
        },
        {
            title: '商品库存',
            width: 130,
            dataIndex: 'stock',
            render: (val, record) => (val ? `${val}${record.unit_name}` : '-'),
        },
        {
            title: '商品编码',
            width: 130,
            dataIndex: 'goods_code',
        },
        {
            title: '商品条码',
            width: 130,
            dataIndex: 'goods_barcode',
        },
    ]

    return (
        <BaseDrawer width={1000} isShow onClose={() => layerManage.pop()} title="批量导入商品-导入记录">
            {!requestHandle.data?.list.length ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <Row className={cls()}>
                    <div ref={containerRef} className={cls('left')}>
                        {requestHandle.data?.list.map((record, index) => (
                            <div
                                onClick={() => {
                                    setActiveIndex(index)
                                }}
                                key={record.id}
                                className={cls('left', activeIndex === index ? 'active' : 'unactive')}
                            >
                                <Row data-align-items-center className="title">
                                    <img
                                        style={{
                                            border: `1px solid ${
                                                record.creator.user.gender === 2 ? '#ee5b5b' : '#3c90ff'
                                            }`,
                                        }}
                                        className="avatar"
                                        alt=""
                                        src={record.creator.user.avatar}
                                    />
                                    <div className="nickname">{record.creator.user.nickname}</div>
                                    <div className="realname">({record.creator.user.realname})</div>
                                </Row>
                                <div className="time">{record.create_time}</div>
                                <Row data-align-items-center data-margin-top-6>
                                    <div className="type">{record.source === 0 ? '店铺导入' : '表格导入'}</div>
                                    <div className="count">商品数：{record.goods_imported}</div>
                                </Row>
                            </div>
                        ))}
                    </div>
                    <div className={cls('right')}>
                        <div className={cls('right', 'top')}>
                            <Row
                                data-align-items-center
                                data-cursor-pointer
                                className="con"
                                onClick={() => {
                                    showLayer(<StaffDetail staffId={currentRecord?.creator.id || 0} />)
                                }}
                            >
                                <img
                                    style={{
                                        border: `1px solid ${
                                            currentRecord?.creator.user.gender === 2 ? '#ee5b5b' : '#3c90ff'
                                        }`,
                                    }}
                                    className="avatar"
                                    alt=""
                                    src={currentRecord?.creator.user.avatar}
                                />
                                <div className="title" data-margin-left-6>
                                    操作人：
                                </div>
                                <div className="nickname">{currentRecord?.creator.user.nickname}</div>
                                <div className="realname">({currentRecord?.creator.user.realname})</div>
                            </Row>
                            <Row data-align-items-center>
                                <div className="type">{currentRecord?.source === 1 ? '表格导入' : '店铺导入'}</div>
                                <Row data-align-items-center data-margin-left-5>
                                    <div className="title">导入商品（条）：</div>
                                    <div data-color-333>{currentRecord?.goods_imported}</div>
                                </Row>
                            </Row>
                        </div>
                        <Expaned className={cls('right', 'table')}>
                            <AutoResizer>
                                {({ height }) => {
                                    return (
                                        <WithScrollLoad data-expaned data-overflow-hidden scrollloadhandle={detailReq}>
                                            <Table
                                                rowKey={record => record.goods_barcode}
                                                loading={
                                                    detailReq.loading ||
                                                    detailReq.loadingMore ||
                                                    requestHandle.loading ||
                                                    void 0
                                                }
                                                bordered
                                                pagination={false}
                                                columns={columns}
                                                dataSource={detailReq.data?.list}
                                                scroll={{ x: '706px', y: height - 50 }}
                                            />
                                        </WithScrollLoad>
                                    )
                                }}
                            </AutoResizer>
                        </Expaned>
                    </div>
                </Row>
            )}
        </BaseDrawer>
    )
}

export default ImportRecord
